import { zodResolver } from '@hookform/resolvers/zod'
import { Check, FolderKanban, Loader2 } from 'lucide-react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import { ReadonlyValue } from '@/components/common/FormBits'
import { Button } from '@/components/ui/button'
import { DateField } from '@/components/ui/date-field'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Modal } from '@/components/ui/modal'
import { FormSelect } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { isApiError } from '@/lib/api/errors'
import { cn } from '@/lib/utils'
import { useSaveProject } from '../api'
import { useSectors } from '../catalogApi'
import { PROJECT_COLORS, PROJECT_ICONS, PROJECT_KINDS, PROJECT_STATUSES, type Project } from '../types'
import { toastError } from '../ui'
import { PROJECT_COLOR, PROJECT_ICON } from '../visuals'

type Props = { open: boolean; project: Project | null; onOpenChange: (v: boolean) => void; onCreated?: (id: string) => void }

export function ProjectFormModal({ open, project, onOpenChange, onCreated }: Props) {
  return open ? <ProjectForm key={project?.id ?? 'new'} project={project} onDone={() => onOpenChange(false)} onCreated={onCreated} /> : null
}

function ProjectForm({ project, onDone, onCreated }: { project: Project | null; onDone: () => void; onCreated?: (id: string) => void }) {
  const { t } = useTranslation()
  const save = useSaveProject()

  const schema = z
    .object({
      name: z.string().trim().min(2, t('projects.form.nameRequired')).max(80),
      key: z.string().trim().regex(/^[A-Za-z]{2,5}$/, t('projects.form.keyInvalid')),
      description: z.string().trim().max(240),
      icon: z.enum(PROJECT_ICONS),
      color: z.enum(PROJECT_COLORS),
      status: z.enum(PROJECT_STATUSES),
      kind: z.enum(PROJECT_KINDS),
      sectorId: z.string().min(1, t('projects.form.sectorRequired')),
      startDate: z.string().min(1, t('projects.form.dateRequired')),
      // İç projede bitiş yoktur (süresiz); özel projede zorunludur.
      endDate: z.string(),
    })
    .refine((v) => v.kind !== 'custom' || !!v.endDate, { path: ['endDate'], message: t('projects.form.endRequiredCustom') })
    .refine((v) => !v.startDate || !v.endDate || v.endDate >= v.startDate, { path: ['endDate'], message: t('projects.form.endBeforeStart') })

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: project?.name ?? '',
      key: project?.key ?? '',
      description: project?.description ?? '',
      icon: project?.icon ?? 'monitor',
      color: project?.color ?? 'navy',
      status: project?.status ?? 'active',
      kind: project?.kind ?? 'internal',
      sectorId: project?.sectorId ?? '',
      startDate: project?.startDate ?? '',
      endDate: project?.endDate ?? '',
    },
  })
  const { errors } = form.formState
  const color = useWatch({ control: form.control, name: 'color' })
  const startDate = useWatch({ control: form.control, name: 'startDate' })
  const kind = useWatch({ control: form.control, name: 'kind' })
  const sectors = useSectors().data ?? []

  const onSubmit = form.handleSubmit(async (input) => {
    try {
      const saved = await save.mutateAsync({ id: project?.id, input })
      toast.success(t(project ? 'projects.updated' : 'projects.created'))
      if (!project) onCreated?.(saved.id)
      onDone()
    } catch (e) {
      if (isApiError(e) && e.code === 'PROJECT_NAME_TAKEN') form.setError('name', { message: t('errors.PROJECT_NAME_TAKEN') })
      else if (isApiError(e) && e.code === 'PROJECT_KEY_TAKEN') form.setError('key', { message: t('errors.PROJECT_KEY_TAKEN') })
      else if (isApiError(e) && e.code === 'PROJECT_END_REQUIRED') form.setError('endDate', { message: t('projects.form.endRequiredCustom') })
      else if (isApiError(e) && e.code === 'PROJECT_END_BEFORE_TASKS') form.setError('endDate', { message: t('projects.form.endBeforeTasks') })
      else toastError(e)
    }
  })

  return (
    <Modal
      open
      size="lg"
      onOpenChange={(v) => !v && onDone()}
      dismissible={!save.isPending}
      icon={<FolderKanban />}
      title={t(project ? 'projects.form.editTitle' : 'projects.form.createTitle')}
      description={t('projects.form.hint')}
      onSubmit={(e) => void onSubmit(e)}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onDone}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? <Loader2 className="animate-spin" /> : null}
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_8rem]">
          <div className="space-y-2">
            <Label htmlFor="prj-name">{t('projects.fields.name')}</Label>
            <Input id="prj-name" aria-invalid={!!errors.name} {...form.register('name')} />
            {errors.name ? <p className="text-xs text-destructive">{errors.name.message}</p> : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor="prj-key">{t('projects.fields.key')}</Label>
            <Input id="prj-key" className="font-mono uppercase" maxLength={5} aria-invalid={!!errors.key} aria-describedby="prj-key-hint" {...form.register('key')} />
            {errors.key ? <p className="text-xs text-destructive">{errors.key.message}</p> : <p id="prj-key-hint" className="text-[11px] text-muted-foreground">{t('projects.form.keyHint')}</p>}
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="prj-desc">{t('projects.fields.description')}</Label>
          <Textarea id="prj-desc" rows={2} maxLength={240} {...form.register('description')} />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <fieldset className="space-y-2">
            <legend className="mb-2 text-sm font-medium">{t('projects.fields.icon')}</legend>
            <Controller
              control={form.control}
              name="icon"
              render={({ field }) => (
                <div className="flex flex-wrap gap-2">
                  {PROJECT_ICONS.map((key) => {
                    const Icon = PROJECT_ICON[key]
                    const active = field.value === key
                    return (
                      <button
                        key={key}
                        type="button"
                        aria-pressed={active}
                        aria-label={t(`projects.icons.${key}`)}
                        onClick={() => field.onChange(key)}
                        className={cn(
                          'flex size-10 items-center justify-center rounded-lg border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                          active ? cn(PROJECT_COLOR[color], 'border-transparent') : 'bg-card text-muted-foreground hover:bg-muted',
                        )}
                      >
                        <Icon className="size-5" />
                      </button>
                    )
                  })}
                </div>
              )}
            />
          </fieldset>
          <fieldset className="space-y-2">
            <legend className="mb-2 text-sm font-medium">{t('projects.fields.color')}</legend>
            <Controller
              control={form.control}
              name="color"
              render={({ field }) => (
                <div className="flex flex-wrap gap-2">
                  {PROJECT_COLORS.map((key) => (
                    <button
                      key={key}
                      type="button"
                      aria-pressed={field.value === key}
                      aria-label={t(`projects.colors.${key}`)}
                      onClick={() => field.onChange(key)}
                      className={cn(
                        'flex size-10 items-center justify-center rounded-full ring-offset-2 ring-offset-background transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        PROJECT_COLOR[key],
                        field.value === key && 'ring-2 ring-foreground/60',
                      )}
                    >
                      {field.value === key ? <Check className="size-4" /> : null}
                    </button>
                  ))}
                </div>
              )}
            />
          </fieldset>
        </div>

        <fieldset className="space-y-2">
          <legend className="mb-2 text-sm font-medium">{t('projects.fields.kind')}</legend>
          <Controller
            control={form.control}
            name="kind"
            render={({ field }) => (
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" role="radiogroup">
                {PROJECT_KINDS.map((k) => (
                  <button
                    key={k}
                    type="button"
                    role="radio"
                    aria-checked={field.value === k}
                    onClick={() => field.onChange(k)}
                    className={cn(
                      'rounded-lg border px-3 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      field.value === k ? 'border-primary/50 bg-primary/[0.06] ring-1 ring-primary/30' : 'bg-card hover:bg-muted',
                    )}
                  >
                    <span className="block text-sm font-medium">{t(`projects.kind.${k}`)}</span>
                    <span className="block text-xs text-muted-foreground">{t(`projects.kindHint.${k}`)}</span>
                  </button>
                ))}
              </div>
            )}
          />
        </fieldset>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="prj-sector">{t('projects.fields.sector')}</Label>
            <FormSelect
              id="prj-sector"
              control={form.control}
              name="sectorId"
              searchable
              placeholder={t('projects.form.sectorPlaceholder')}
              options={sectors.map((s) => ({ value: s.id, label: s.name, description: s.description }))}
            />
            {errors.sectorId ? <p className="text-xs text-destructive">{errors.sectorId.message}</p> : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor="prj-status">{t('projects.fields.status')}</Label>
            <FormSelect id="prj-status" control={form.control} name="status" options={PROJECT_STATUSES.map((s) => ({ value: s, label: t(`projects.status.${s}`) }))} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="prj-deploy">{t('projects.fields.deployment')}</Label>
            <ReadonlyValue id="prj-deploy">{project?.deploymentType ? t(`projects.deployment.${project.deploymentType}`) : t('projects.deploymentNone')}</ReadonlyValue>
            <p className="text-xs text-muted-foreground">{t('projects.deploymentDerived')}</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="prj-start">{t('projects.fields.startDate')}</Label>
            <Controller
              control={form.control}
              name="startDate"
              render={({ field }) => <DateField id="prj-start" value={field.value} onChange={field.onChange} aria-invalid={!!errors.startDate} />}
            />
            {errors.startDate ? <p className="text-xs text-destructive">{errors.startDate.message}</p> : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor="prj-end">{t(kind === 'custom' ? 'projects.fields.endDate' : 'projects.fields.endDateOptional')}</Label>
            <Controller
              control={form.control}
              name="endDate"
              render={({ field }) => <DateField id="prj-end" value={field.value} onChange={field.onChange} min={startDate || undefined} aria-invalid={!!errors.endDate} />}
            />
            {errors.endDate ? (
              <p className="text-xs text-destructive">{errors.endDate.message}</p>
            ) : (
              <p className="text-xs text-muted-foreground">{t(kind === 'custom' ? 'projects.form.endHintCustom' : 'projects.form.endHintInternal')}</p>
            )}
          </div>
        </div>
      </div>
    </Modal>
  )
}
